import { OrderItemMetadata } from '@/components/OrderItemMetadata';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useOrderDetails } from '@/hooks/useOrder';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { getOrderStatusBadgeClass, getOrderStatusLabel } from '@/lib/order-status';
import { formatPrice } from '@/lib/format';



export function OrderDetailView() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const { data: order, isLoading, error } = useOrderDetails(orderId!);

  if (isLoading) {
    return (
      <div className="container py-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container py-8">
        <div className="text-red-500">Error loading order details</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f7f8] py-4">
      <div className=" mx-auto">
        {/* Bouton retour */}
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                Commande #{order.orderNumber}
              </h1>
              <p className="text-muted-foreground">
                Voir le détail et le statut de la commande
              </p>
            </div>
            <Badge
              className={`${getOrderStatusBadgeClass(
                order.status
              )} px-3 py-1 text-sm font-semibold`}
            >
              {getOrderStatusLabel(order.status)}
            </Badge>
          </div>

          {/* Infos principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div>
                <span className="block text-xs text-muted-foreground">
                  Date
                </span>
                <span className="font-medium">
                  {format(new Date(order.createdAt), 'PPP', { locale: fr })}
                </span>
              </div>
              {order.createdBy && (
                <div>
                  <span className="block text-xs text-muted-foreground">
                    Client
                  </span>
                  <span className="font-medium">
                    {[order.createdBy.firstName, order.createdBy.lastName]
                      .filter(Boolean)
                      .join(' ') || '-'}
                  </span>
                </div>
              )}
              <div>
                <span className="block text-xs text-muted-foreground">
                  Total
                </span>
                <span className="font-medium text-lg">
                  {formatPrice(order.total)}
                </span>
              </div>
              <div>
                <span className="block text-xs text-muted-foreground">
                  Adresse de livraison
                </span>

                <div className="bg-gray-50 rounded-lg p-3 mt-1 text-sm flex flex-col gap-2">
                  <span className="font-medium">{order.organization.name}</span>
                  {order.deliveryAddress && !order.deliveryAddress?.isProfessional && (
                    <Badge variant="outline" className="mb-2">
                      Adresse personnelle
                    </Badge>
                  )}

                  <div className="flex flex-col">
                    {order.deliveryAddress?.isProfessional && (
                      <div>
                        Entreprise:{' '}
                        <span className="font-medium">
                          {order.deliveryAddress?.companyName}
                        </span>
                      </div>
                    )}
                    {order.deliveryAddress?.recipientName && (
                      <div>
                        Destinataire:{' '}
                        <span className="font-medium">
                          {order.deliveryAddress?.recipientName}
                        </span>
                      </div>
                    )}
                    <div>{order.deliveryAddress?.street}</div>
                    <div>
                      {order.deliveryAddress?.zipCode}{' '}
                      {order.deliveryAddress?.city}
                    </div>
                    <div>{order.deliveryAddress?.country}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Articles */}
            <div>
              <span className="block text-xs text-muted-foreground mb-2">
                Articles
              </span>
              <div className="divide-y rounded-lg border bg-gray-50">
                {order.items.map((item) => {
                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-between px-4 py-3"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{item.name}</div>
                        {item.description && (
                          <div className="text-xs text-gray-500">
                            {item.description}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          {item.quantity} ×{' '}
                          {formatPrice(item.price)}
                          {item.isRecurring ? (
                            <span className="text-blue-600 ml-1">/mois</span>
                          ) : (
                            <span className="text-gray-500 ml-1">à l'acte</span>
                          )}
                        </div>
                        {/* Add metadata display */}
                        <OrderItemMetadata item={item} orderId={order.id} />
                      </div>

                      <div className="flex flex-col items-end gap-2 ml-4">
                        <span className="font-semibold text-sm">
                          {formatPrice(item.total)}
                          {item.isRecurring ? (
                            <span className="text-blue-600 ml-1">/mois</span>
                          ) : (
                            <span className="text-gray-500 ml-1">à l'acte</span>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Numéro de suivi TNT */}
              {order.shippingMethod === 'TNT' && order.trackingNumber && (
                <div className="mt-4">
                  <span className="block text-xs text-muted-foreground mb-2">
                    Numéro de suivi TNT
                  </span>
                  <div className="rounded-lg border bg-gray-50 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-1">
                        <span className="font-mono text-sm font-medium">
                          {order.trackingNumber}
                        </span>
                        <a
                          href={`https://www.tnt.fr/public/suivi_colis/recherche/visubontransport.do?bonTransport=${order.trackingNumber}&bonTransportTemp=&radiochoixrecherche=BT&radiochoixtypeexpedition=NAT&refInterneInt=`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
                        >
                          Suivre mon colis sur TNT.fr
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Totaux */}
            <div className="mt-6 space-y-2">
              {order.fixedTotal > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Total HT (à l'acte)
                  </span>
                  <span className="font-medium">
                    {formatPrice(order.fixedTotal - (order.shippingCost ?? 0))}
                  </span>
                </div>
              )}
              {order.recurringTotal > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Total HT/mois (forfaits)
                  </span>
                  <span className="font-medium">
                    {formatPrice(order.recurringTotal)}
                  </span>
                </div>
              )}
              {order.shippingCost && order.shippingCost > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Frais de port
                    {order.shippingMethod === 'LETTRE_SIMPLE'
                      ? ' (Lettre simple)'
                      : order.shippingMethod === 'TNT'
                        ? ' (TNT)'
                        : ''}
                  </span>
                  <span className="font-medium">
                    {formatPrice(order.shippingCost)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between border-t pt-2">
                <span className="font-medium">Total</span>
                <span className="text-lg font-bold">
                  {formatPrice(order.total)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
